import noop from 'lodash/noop'
import { RefObject, useEffect, useState } from 'react'

interface IntersectingChildrenObserverConfig {
  attributeName: string | 'data-intersection-id'
  rootMargin: string | '500px 0px 500px 0px'
}

export const useIntersectingChildrenObserver = (
  ref: RefObject<HTMLDivElement>,
  config: IntersectingChildrenObserverConfig = { attributeName: 'data-intersection-id', rootMargin: '500px 0px 500px 0px' },
) => {
  const [currentRef, setCurrentRef] = useState(ref?.current)
  const [state, setState] = useState<Record<string, boolean>>({})
  useEffect(() => {
    setCurrentRef((prev) => {
      if (prev === ref.current) {
        return prev
      }

      return ref.current
    })
  }, [ref])

  useEffect(() => {
    if (currentRef) {
      const intersectionObserver = new IntersectionObserver(
        (entries) => {
          const next: Record<string, boolean> = {}
          entries.forEach((entry) => {
            const intersectionID = entry.target.getAttribute(config.attributeName) || ''
            if (!intersectionID) {
              return
            }
            next[intersectionID] = entry.isIntersecting
          })

          setState(prev => ({ ...prev, ...next }))
        },
        {
          rootMargin: config.rootMargin,
          threshold: 0,
        },
      )

      const mutationObserver = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.target !== currentRef) {
            return
          }

          mutation.addedNodes.forEach(element => intersectionObserver.observe(element as Element))
          mutation.removedNodes.forEach(element => intersectionObserver.unobserve(element as Element))
        })
      })

      mutationObserver.observe(currentRef, {
        childList: true,
      })

      if (currentRef.children) {
        Array.from(currentRef.children).forEach(element => intersectionObserver.observe(element))
      }

      return () => {
        intersectionObserver.disconnect()
      }
    }

    return noop
  }, [config.attributeName, config.rootMargin, currentRef])

  return state
}
